<template>
  <div class="travel-calculator" v-if="coordinates">
    <slot name="top" />
    <slot />

    <LazyLoad @intersect="initTravelCalculator">
      <InputComponent class="travel-calculator__input">
        <div ref="geocoder"></div>
      </InputComponent>
    </LazyLoad>

    <ButtonComponent
      class="travel-calculator__button"
      v-bind="{
        ...buttonOptionsComp,
        label: distanceCalculated ? buttonOptionsComp['reset-label'] : buttonOptionsComp['label'],
      }"
      @click="distanceCalculated ? reset() : getDistance()"
    ></ButtonComponent>

    <Loader :value="promise" theme="default">
      <transition v-if="distanceCalculated" name="fade">
        <div class="travel-calculator__distance">
          <template v-if="hasData">
            <div
              v-for="([key, value], index) in (Object.entries(items) || []).filter(([key, value]) =>
                Boolean(value),
              )"
              :key="index"
              class="travel-calculator__distance-card"
            >
              <div class="travel-calculator__distance-card-icon">
                <FontAwesomeIcon :icon="iconsComp[key]" />
              </div>

              <h4 class="travel-calculator__distance-card-content">
                {{ toHoursAndMinutes(value) }}
              </h4>
            </div>
          </template>

          <div
            v-else
            class="travel-calculator__distance-card travel-calculator__distance-card--error"
          >
            <div class="travel-calculator__distance-card-icon">
              <FontAwesomeIcon :icon="iconsComp['error']" />
            </div>
            <h4 class="travel-calculator__distance-card-content">
              {{ $t('travelTimeError', { project: project }) }}
            </h4>
          </div>
          <p class="travel-calculator__distance-alert">
            * {{ $t('travelTimeIsCalculatedByMapbox') }}
          </p>
        </div>
      </transition>
    </Loader>
    <slot name="bottom" />
  </div>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import { ButtonComponent } from 'vue-elder-button'
import LazyLoad from 'vue-make-lazy'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { LoaderComponent as Loader } from 'vue-elder-loader'

import mapboxgl from 'mapbox-gl'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import 'mapbox-gl/dist/mapbox-gl.css'

import './icons'

export default {
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    project: {
      type: String,
      default: 'prosjektet',
    },
    inputPlaceholder: {
      type: String,
      default: 'Hvilken adresse reiser du fra?',
    },

    buttonOptions: {
      type: Object,
      default: () => ({}),
    },
    hide: {
      type: Object,
      default: () => ({}),
    },
    icons: {
      type: Object,
      default: () => ({}),
    },
    geocoderCountries: {
      type: String,
      default: 'no,se',
    },
    accessToken: {
      type: String,
      default: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
    },
  },
  data() {
    return {
      geocoder: null,
      promise: null,
      selectedCoordinates: null,
      items: {
        cycling: null,

        driving: null,
        walking: null,
        'driving-traffic': null,
      },
      distanceCalculated: false,
    }
  },

  computed: {
    hasData() {
      return Object.keys(this.items).some(key => Boolean(this.items[key]))
    },
    buttonOptionsComp() {
      return {
        //['primary', 'secondary', 'warning', 'success', 'error', 'default']
        theme: 'default',
        icon: null,
        //['left', 'right'],
        'icon-placement': 'right',
        label: 'Beregn',
        'reset-label': 'Prøv igjen',

        ...this.buttonOptions,
      }
    },
    hideComp() {
      return {
        driving: false,
        'driving-traffic': false,
        walking: false,
        cycling: false,
        ...this.hide,
      }
    },
    iconsComp() {
      return {
        driving: ['fas', 'car-alt'],
        'driving-traffic': ['fas', 'hashtag'],
        walking: ['fas', 'walking'],
        cycling: ['fas', 'bicycle'],
        error: ['fas', 'times'],

        ...this.icons,
      }
    },
    coordinates() {
      if (!this.$path('data.location.coordinates') && !this.$path('data.coordinates')) return null
      return (this.$path('data.location.coordinates') || this.$path('data.coordinates')).toString()
    },
  },

  methods: {
    async initTravelCalculator() {
      try {
        mapboxgl.accessToken = this.accessToken

        this.geocoder = new MapboxGeocoder({
          accessToken: this.accessToken,
          mapboxgl,
          center: this.coordinates,
          autocomplete: true,
          types: 'address',
          marker: false,
          countries: this.geocoderCountries,
          placeholder: this.inputPlaceholder,
        })

        this.geocoder.addTo(this.$refs.geocoder)
        this.geocoder.on('result', e => {
          this.selectedCoordinates = e.result.geometry.coordinates.toString()
        })
      } catch (err) {
        console.log('geocode error', err)
      }
    },

    fetchURL(type) {
      return `https://api.mapbox.com/directions/v5/mapbox/${type}/${this.coordinates};${this.selectedCoordinates}?access_token=${this.accessToken}`
    },

    getPromise(type, hide) {
      if (hide) return ''
      return fetch(this.fetchURL(type)).then(response => response.json())
    },

    getDistance() {
      if (!this.coordinates || !this.selectedCoordinates) return
      this.promise = Promise.all(
        Object.keys(this.items).map(key => {
          return this.getPromise(key, this.hideComp[key]).then(result => {
            this.items[key] = result.routes?.[0]?.duration
          })
        }),
      )
        .then(() => {
          this.distanceCalculated = true
        })
        .catch(err => console.error(err))
    },

    reset() {
      this.items = Object.fromEntries(Object.keys(this.items).map(key => [key, null]))
      this.promise = null
      this.geocoder.clear()

      this.distanceCalculated = false
    },

    toHoursAndMinutes(totalSeconds) {
      if (isNaN(totalSeconds)) return totalSeconds

      const totalMinutes = Math.floor(totalSeconds / 60)

      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      let total = ''

      if (hours !== 0) total += `${hours} ${hours === 1 ? 'hour' : 'hours'} `
      if (minutes !== 0) total += `${minutes} ${minutes === 1 ? 'min' : 'mins'}`

      return total
    },
  },

  components: {
    InputComponent,
    ButtonComponent,
    LazyLoad,
    FontAwesomeIcon,
    Loader,
  },
}
</script>

<style lang="scss">
.travel-calculator {
  $breakAt: 767px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3rem;

  &__input {
    margin: 0 auto;
    border: none;

    max-width: 600px;
    position: relative;
    z-index: 1000;

    .elder-input__field {
      background: transparent;
      border: none;
    }

    .mapboxgl-ctrl-geocoder {
      width: 100%;
      max-width: none;
      box-shadow: none;
      height: 100%;
      border-radius: 10px;
      font-size: 1rem;
      background: inherit;
      font-family: var(--primary-font);
      font-size: 1.2rem;

      &--button {
        background-color: inherit !important;
      }

      .mapboxgl-ctrl-geocoder--icon-search {
        display: none;
      }

      .mapboxgl-ctrl-geocoder--input {
        padding: 5px 10px 1px;
        height: 100%;
        border-bottom: 1px solid rgba(0, 0, 0, 0.75);
      }
      .mapboxgl-ctrl-geocoder--input:focus {
        outline: 0;
        border-width: 2px;
      }
    }

    .mapboxgl-ctrl-attrib,
    .mapboxgl-ctrl-logo,
    .mapboxgl-ctrl-top-right {
      display: none;
    }

    .mapboxgl-ctrl-top-left {
      position: relative;
      z-index: 0;
    }
  }

  &__button {
    z-index: 1;
    margin: 0 auto;
  }

  &__distance {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 22px;
    max-width: 622px;
    margin: 0 auto;

    @media (max-width: #{$breakAt}) {
      grid-template-columns: 1fr;
    }

    &-card {
      color: var(--primary);
      background-color: rgb(255, 255, 255);
      border-radius: 10px;
      padding: 10px 20px;
      gap: 20px;
      display: flex;
      align-items: center;

      &--error {
        grid-column-end: span 2;

        @media (max-width: #{$breakAt}) {
          grid-column-end: span 1;
        }
      }

      &-icon {
        text-align: center;
        font-size: 4.5rem;
        svg {
          height: 1em;
          width: 1em !important;
          padding: 0.5rem;
          border: 1px solid var(--primary);
          border-radius: 100%;
        }
      }
      &-content {
        margin: 0 0 10px;
        font-size: 26px;
        line-height: 28px;
        font-weight: normal;
      }
    }

    &-alert {
      text-align: center;
      grid-column-end: span 2;
      margin: 0;
      font-size: 1rem;

      @media (max-width: #{$breakAt}) {
        grid-column-end: span 1;
      }
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.5s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
